import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import Navigation from "../components/Navigation";
import PostCard from "../components/PostCard";
import TransplantSettings, { TransplantBadge } from "../components/TransplantSettings";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import {
  Edit2,
  Trophy,
  Target,
  LogOut,
  Camera,
  Loader2,
  Palette,
  Image,
  Type,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Square,
  Circle,
  Sparkles,
  Check,
  Upload,
  Heart,
  Film,
  Music,
  MapPin,
  Quote,
  BookOpen,
  Star,
  Plus,
  X,
  User,
  Ribbon,
  MessageSquare,
  Send,
  Trash2,
  Users,
  UserPlus,
  Ban,
} from "lucide-react";
import { toast } from "sonner";

const TIER_STYLES = {
  Beginner: "tier-beginner",
  Bronze: "tier-bronze",
  Silver: "tier-silver",
  Gold: "tier-gold",
  Champion: "tier-champion",
};

const FONT_STYLES = {
  modern: { family: "'Outfit', sans-serif", name: "Modern" },
  classic: { family: "'Georgia', serif", name: "Classic" },
  playful: { family: "'Caveat', cursive", name: "Playful" },
};

export default function Profile() {
  const { userId } = useParams();
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const bannerInputRef = useRef(null);

  const [profileUser, setProfileUser] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", bio: "" });
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  
  // Theme customization state
  const [themePresets, setThemePresets] = useState({});
  const [currentTheme, setCurrentTheme] = useState({
    primary_color: "#D97706",
    secondary_color: "#F59E0B",
    background_type: "gradient",
    background_value: "linear-gradient(135deg, #fef3c7 0%, #fde68a 50%, #fcd34d 100%)",
    card_style: "rounded",
    font_style: "modern",
    bio_alignment: "left",
    banner_image: null,
    preset_theme: "golden",
  });
  const [savingTheme, setSavingTheme] = useState(false);

  // About You state
  const [aboutYou, setAboutYou] = useState({
    motivations: [],
    likes: [],
    favorite_movies: [],
    favorite_songs: [],
    favorite_music_groups: [],
    places_to_visit: [],
    favorite_quotes: [],
    hobbies: [],
    life_goals: "",
    favorite_books: [],
  });
  const [savingAbout, setSavingAbout] = useState(false);
  const [newItem, setNewItem] = useState({});

  // Transplant awareness state
  const [transplantOpen, setTransplantOpen] = useState(false);
  const [transplantInfo, setTransplantInfo] = useState(null);

  // Wall posts state
  const [wallPosts, setWallPosts] = useState([]);
  const [wallPostContent, setWallPostContent] = useState("");
  const [postingToWall, setPostingToWall] = useState(false);
  const [loadingWall, setLoadingWall] = useState(false);

  // Friendship state
  const [friendshipStatus, setFriendshipStatus] = useState("none"); // none, friends, pending_sent, pending_received
  const [friendCount, setFriendCount] = useState(0);

  const isOwnProfile = !userId || userId === user?.id;

  useEffect(() => {
    fetchProfile();
    fetchPosts();
    fetchThemePresets();
    fetchWallPosts();
    if (isOwnProfile) {
      fetchAboutYou();
      fetchTransplantInfo();
    } else if (userId) {
      fetchUserTransplantInfo(userId);
      fetchAboutYou();
      checkFriendshipStatus();
    }
    fetchFriendCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, user?.id]);

  const fetchTransplantInfo = async () => {
    try {
      const response = await axios.get(`${API}/users/transplant-info`);
      setTransplantInfo(response.data.transplant_info);
    } catch (error) {
      console.error("Failed to load transplant info");
    }
  };

  const fetchUserTransplantInfo = async (uid) => {
    try {
      const response = await axios.get(`${API}/users/${uid}/transplant-info`);
      setTransplantInfo(response.data.transplant_info);
    } catch (error) {
      console.error("Failed to load transplant info");
    }
  };

  const fetchProfile = async () => {
    try {
      if (isOwnProfile) {
        setProfileUser(user);
        setEditForm({ name: user?.name || "", bio: user?.bio || "" });
        if (user?.theme) {
          setCurrentTheme({ ...currentTheme, ...user.theme });
        }
      } else {
        const response = await axios.get(`${API}/users/${userId}`);
        setProfileUser(response.data);
      }
    } catch (error) {
      toast.error("Failed to load profile");
      navigate("/feed");
    }
  };

  const fetchPosts = async () => {
    try {
      const targetId = isOwnProfile ? user?.id : userId;
      const response = await axios.get(`${API}/users/${targetId}/posts`);
      setPosts(response.data);
    } catch (error) {
      console.error("Failed to load posts");
    } finally {
      setLoading(false);
    }
  };

  const fetchThemePresets = async () => {
    try {
      const response = await axios.get(`${API}/users/theme-presets`);
      setThemePresets(response.data);
    } catch (error) {
      console.error("Failed to load theme presets");
    }
  };

  const fetchWallPosts = async () => {
    setLoadingWall(true);
    try {
      const targetId = isOwnProfile ? user?.id : userId;
      if (!targetId) return;
      const response = await axios.get(`${API}/users/${targetId}/wall`);
      setWallPosts(response.data.posts || []);
    } catch (error) {
      console.error("Failed to load wall posts");
    } finally {
      setLoadingWall(false);
    }
  };

  const checkFriendshipStatus = async () => {
    try {
      // Use search endpoint to check friendship status
      const response = await axios.get(`${API}/users/search?q=${userId}`);
      const foundUser = response.data.users?.find(u => u.id === userId);
      if (foundUser) {
        setFriendshipStatus(foundUser.friendship_status || "none");
      }
    } catch (error) {
      console.error("Failed to check friendship status");
    }
  };

  const fetchFriendCount = async () => {
    try {
      const targetId = isOwnProfile ? user?.id : userId;
      if (!targetId) return;
      const response = await axios.get(`${API}/friends/${targetId}`);
      setFriendCount(response.data.count || 0);
    } catch (error) {
      console.error("Failed to load friend count");
    }
  };

  const postToWall = async () => {
    if (!wallPostContent.trim()) return;
    setPostingToWall(true);
    try {
      const targetId = isOwnProfile ? user?.id : userId;
      await axios.post(`${API}/users/${targetId}/wall`, { content: wallPostContent });
      toast.success("Posted to wall!");
      setWallPostContent("");
      fetchWallPosts();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to post";
      toast.error(message);
    } finally {
      setPostingToWall(false);
    }
  };

  const likeWallPost = async (postId) => {
    try {
      await axios.post(`${API}/wall/${postId}/like`);
      fetchWallPosts();
    } catch (error) {
      toast.error("Failed to like post");
    }
  };

  const deleteWallPost = async (postId) => {
    if (!confirm("Delete this wall post?")) return;
    try {
      await axios.delete(`${API}/wall/${postId}`);
      toast.success("Post deleted");
      fetchWallPosts();
    } catch (error) {
      toast.error("Failed to delete post");
    }
  };

  const sendFriendRequest = async () => {
    try {
      await axios.post(`${API}/friends/request`, { to_user_id: userId });
      toast.success("Friend request sent!");
      setFriendshipStatus("pending_sent");
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to send request";
      toast.error(message);
    }
  };

  const fetchAboutYou = async () => {
    try {
      const response = await axios.get(`${API}/users/about`);
      if (response.data && Object.keys(response.data).length > 0) {
        setAboutYou(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error("Failed to load about you");
    }
  };

  const saveAboutYou = async () => {
    setSavingAbout(true);
    try {
      await axios.put(`${API}/users/about`, aboutYou);
      toast.success("About You saved! ✨");
      setAboutOpen(false);
    } catch (error) {
      toast.error("Failed to save About You");
    } finally {
      setSavingAbout(false);
    }
  };

  const addToList = (field) => {
    if (newItem[field]?.trim()) {
      setAboutYou(prev => ({
        ...prev,
        [field]: [...(prev[field] || []), newItem[field].trim()]
      }));
      setNewItem(prev => ({ ...prev, [field]: "" }));
    }
  };

  const removeFromList = (field, index) => {
    setAboutYou(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.put(`${API}/users/profile`, editForm);
      updateUser(response.data);
      setProfileUser(response.data);
      setEditOpen(false);
      toast.success("Profile updated!");
    } catch (error) {
      toast.error("Failed to update profile");
    }
  };

  const handleSaveTheme = async () => {
    setSavingTheme(true);
    try {
      const response = await axios.put(`${API}/users/theme`, currentTheme);
      updateUser(response.data);
      setProfileUser(response.data);
      setCustomizeOpen(false);
      toast.success("Profile theme saved! Looking amazing! ✨");
    } catch (error) {
      toast.error("Failed to save theme");
    } finally {
      setSavingTheme(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image too large. Max 5MB allowed");
      return;
    }

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(`${API}/upload/profile-photo`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const photoUrl = response.data.url;
      updateUser({ profile_photo: photoUrl });
      setProfileUser((prev) => ({ ...prev, profile_photo: photoUrl }));
      toast.success("Profile photo updated! Looking great! ✨");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload photo");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleBannerUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      console.log("No file selected");
      return;
    }

    console.log("File selected:", file.name, file.type, file.size);

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image too large. Max 10MB allowed");
      return;
    }

    setUploadingBanner(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      console.log("Uploading banner...");
      const response = await axios.post(`${API}/upload/banner`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      console.log("Upload response:", response.data);
      const bannerUrl = response.data.url;
      
      // Update current theme state
      setCurrentTheme((prev) => ({ ...prev, banner_image: bannerUrl }));
      
      // Also update the profileUser to show immediately
      setProfileUser((prev) => ({
        ...prev,
        theme: { ...(prev?.theme || {}), banner_image: bannerUrl }
      }));
      
      // Update user context
      updateUser({ 
        theme: { ...(user?.theme || {}), banner_image: bannerUrl } 
      });
      
      toast.success("Cover photo updated! 📸");
      
      // Reset the file input so the same file can be selected again
      if (bannerInputRef.current) {
        bannerInputRef.current.value = "";
      }
    } catch (error) {
      console.error("Banner upload error:", error);
      toast.error(error.response?.data?.detail || "Failed to upload cover photo");
    } finally {
      setUploadingBanner(false);
    }
  };

  const applyPreset = (presetKey) => {
    if (themePresets.presets && themePresets.presets[presetKey]) {
      const preset = themePresets.presets[presetKey];
      setCurrentTheme((prev) => ({
        ...prev,
        primary_color: preset.primary_color,
        secondary_color: preset.secondary_color,
        background_type: preset.background_type,
        background_value: preset.background_value,
        preset_theme: presetKey,
      }));
    }
  };

  const handleReaction = async (postId, reactionType) => {
    try {
      const response = await axios.post(`${API}/posts/${postId}/react`, {
        reaction_type: reactionType,
      });
      setPosts((prev) =>
        prev.map((p) => (p.id === postId ? response.data.post : p))
      );
    } catch (error) {
      toast.error("Failed to react");
    }
  };

  const handleDelete = async (postId) => {
    try {
      await axios.delete(`${API}/posts/${postId}`);
      setPosts((prev) => prev.filter((p) => p.id !== postId));
      toast.success("Post deleted");
    } catch (error) {
      toast.error("Failed to delete post");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("See you soon! Stay positive! 🌟");
  };

  if (!profileUser) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-24 h-24 rounded-full bg-muted"></div>
          <div className="h-6 w-40 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  const getPhotoUrl = (photo) => {
    if (!photo) return null;
    if (photo.startsWith("http")) return photo;
    return `${process.env.REACT_APP_BACKEND_URL}${photo}`;
  };

  // Get the theme to display (from profileUser or current editing theme)
  const displayTheme = profileUser?.theme || currentTheme;
  
  // Generate dynamic styles based on theme
  const profileStyles = {
    "--profile-primary": displayTheme.primary_color || "#D97706",
    "--profile-secondary": displayTheme.secondary_color || "#F59E0B",
  };

  const cardClass = {
    rounded: "rounded-3xl",
    sharp: "rounded-none",
    glass: "rounded-3xl backdrop-blur-xl bg-white/70",
  }[displayTheme.card_style || "rounded"];

  const fontFamily = FONT_STYLES[displayTheme.font_style || "modern"]?.family;

  const bioAlignment = {
    left: "text-left",
    center: "text-center",
    right: "text-right",
  }[displayTheme.bio_alignment || "left"];

  // Get avatar border style
  const avatarBorderStyle = displayTheme.avatar_border || "solid";
  const avatarBorderClass = {
    solid: "border-4",
    double: "border-[6px] ring-2 ring-offset-2",
    glow: "border-4 shadow-[0_0_20px_rgba(251,191,36,0.6)]",
    rainbow: "border-4 bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 p-1",
    none: "border-0",
  }[avatarBorderStyle] || "border-4";

  // Get name effect style
  const nameEffect = displayTheme.name_effect || "none";
  const getNameStyle = () => {
    switch (nameEffect) {
      case "gradient":
        return {
          background: `linear-gradient(135deg, ${displayTheme.primary_color}, ${displayTheme.secondary_color})`,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          backgroundClip: "text",
        };
      case "glow":
        return {
          textShadow: `0 0 10px ${displayTheme.primary_color}, 0 0 20px ${displayTheme.primary_color}40`,
        };
      case "shadow":
        return {
          textShadow: `2px 2px 4px rgba(0,0,0,0.3)`,
        };
      case "outline":
        return {
          WebkitTextStroke: `1px ${displayTheme.primary_color}`,
          color: "transparent",
        };
      default:
        return {};
    }
  };

  return (
    <div className="min-h-screen bg-background pb-24" style={profileStyles}>
      {/* Profile Header - Facebook Style Cover Photo */}
      <div className="relative">
        {/* Cover/Banner - Made taller with solid background behind to handle transparent images */}
        <div
          className="h-64 md:h-80 lg:h-96 relative overflow-hidden"
          style={{
            backgroundColor: "#1a1a2e", // Dark solid color behind everything
          }}
        >
          {/* Vibrant gradient layer */}
          <div
            className="absolute inset-0"
            style={{
              background: `linear-gradient(135deg, ${displayTheme.primary_color || "#D97706"} 0%, ${displayTheme.secondary_color || "#F59E0B"} 50%, #fcd34d 100%)`,
            }}
          />
          {/* Actual cover image on top (if exists) */}
          {displayTheme.banner_image && (
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: `url(${getPhotoUrl(displayTheme.banner_image)})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
              }}
            />
          )}
          {/* Gradient overlay for better text visibility */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/20 to-transparent" />
          
          {/* Cover Photo Actions - Top Right Corner */}
          {isOwnProfile && (
            <div className="absolute top-4 right-4 flex gap-2">
              <input
                type="file"
                ref={bannerInputRef}
                onChange={handleBannerUpload}
                accept="image/*"
                className="hidden"
                data-testid="banner-file-input"
              />
              <button
                onClick={() => bannerInputRef.current?.click()}
                disabled={uploadingBanner}
                className="bg-white/90 backdrop-blur-sm text-foreground px-4 py-2 rounded-lg flex items-center gap-2 shadow-lg hover:bg-white transition-all font-medium"
                data-testid="edit-cover-btn"
              >
                {uploadingBanner ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Camera className="w-4 h-4" />
                )}
                {displayTheme.banner_image ? "Change Cover" : "Add Cover Photo"}
              </button>
              <button
                onClick={() => setCustomizeOpen(true)}
                className="bg-white/90 backdrop-blur-sm text-foreground px-4 py-2 rounded-lg flex items-center gap-2 shadow-lg hover:bg-white transition-all font-medium"
                data-testid="customize-profile-btn"
              >
                <Palette className="w-4 h-4" />
                Customize
              </button>
            </div>
          )}
        </div>

        {/* Profile info */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-20">
          <div
            className={`${cardClass} p-6 shadow-xl border border-border/50`}
            style={{
              backgroundColor: displayTheme.card_style === "glass" ? "rgba(255,255,255,0.7)" : "var(--card)",
              fontFamily,
            }}
          >
            <div className="flex flex-col sm:flex-row items-center sm:items-end gap-6">
              {/* Avatar with upload button */}
              <div className="relative -mt-20 sm:-mt-24">
                <div className={avatarBorderStyle === "rainbow" ? "rounded-full bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 p-1" : ""}>
                  <Avatar
                    className={`w-36 h-36 ${avatarBorderStyle === "rainbow" ? "border-4 border-white" : avatarBorderClass} shadow-xl`}
                    style={{ 
                      borderColor: avatarBorderStyle !== "rainbow" && avatarBorderStyle !== "none" ? displayTheme.primary_color : undefined,
                      boxShadow: avatarBorderStyle === "glow" ? `0 0 30px ${displayTheme.primary_color}80` : undefined,
                    }}
                  >
                    <AvatarImage src={getPhotoUrl(profileUser.profile_photo)} />
                    <AvatarFallback
                      className="text-4xl text-white font-bold"
                      style={{ backgroundColor: displayTheme.primary_color }}
                    >
                      {profileUser.name?.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </div>

                {isOwnProfile && (
                  <>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handlePhotoUpload}
                      accept="image/*"
                      className="hidden"
                      data-testid="profile-photo-input"
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingPhoto}
                      data-testid="upload-photo-btn"
                      className="absolute bottom-1 right-1 w-10 h-10 rounded-full text-white flex items-center justify-center shadow-lg hover:scale-110 transition-all active:scale-95 disabled:opacity-50"
                      style={{ backgroundColor: displayTheme.primary_color }}
                    >
                      {uploadingPhoto ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Camera className="w-5 h-5" />
                      )}
                    </button>
                  </>
                )}
              </div>

              <div className={`flex-1 ${bioAlignment}`}>
                <div className="flex flex-col sm:flex-row items-center gap-3 mb-2">
                  <h1 
                    className="text-3xl font-bold"
                    style={getNameStyle()}
                  >
                    {profileUser.name}
                  </h1>
                  {transplantInfo?.is_seeking && transplantInfo?.show_on_profile && (
                    <TransplantBadge
                      info={transplantInfo}
                      userName={profileUser.name}
                      userPhoto={getPhotoUrl(profileUser.profile_photo)}
                      size="md"
                    />
                  )}
                  <Badge
                    data-testid="profile-tier-badge"
                    className="px-3 py-1"
                    style={{
                      backgroundColor: displayTheme.primary_color,
                      color: "white",
                    }}
                  >
                    {profileUser.tier}
                  </Badge>
                </div>
                <p className="text-muted-foreground mb-4" style={{ fontFamily }}>
                  {profileUser.bio || "No bio yet"}
                </p>

                {/* Stats */}
                <div className={`flex gap-6 ${bioAlignment === "text-center" ? "justify-center" : bioAlignment === "text-right" ? "justify-end" : "justify-start"}`}>
                  <div className="flex items-center gap-2">
                    <Trophy className="w-5 h-5" style={{ color: displayTheme.primary_color }} />
                    <span className="font-semibold">{profileUser.trophies}</span>
                    <span className="text-muted-foreground">Trophies</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Target className="w-5 h-5" style={{ color: displayTheme.secondary_color }} />
                    <span className="font-semibold">{posts.length}</span>
                    <span className="text-muted-foreground">Posts</span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              {isOwnProfile && (
                <div className="flex gap-3">
                  <Dialog open={editOpen} onOpenChange={setEditOpen}>
                    <DialogTrigger asChild>
                      <Button
                        data-testid="edit-profile-btn"
                        variant="outline"
                        className="rounded-full"
                      >
                        <Edit2 className="w-4 h-4 mr-2" />
                        Edit
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Edit Profile</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleUpdateProfile} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="edit-name">Name</Label>
                          <Input
                            id="edit-name"
                            data-testid="edit-name-input"
                            value={editForm.name}
                            onChange={(e) =>
                              setEditForm((prev) => ({
                                ...prev,
                                name: e.target.value,
                              }))
                            }
                            className="input-happy"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="edit-bio">Bio</Label>
                          <Textarea
                            id="edit-bio"
                            data-testid="edit-bio-input"
                            value={editForm.bio}
                            onChange={(e) =>
                              setEditForm((prev) => ({
                                ...prev,
                                bio: e.target.value,
                              }))
                            }
                            className="input-happy"
                            placeholder="Tell us about yourself..."
                          />
                        </div>
                        <Button
                          type="submit"
                          className="w-full btn-primary"
                          data-testid="save-profile-btn"
                        >
                          Save Changes
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>

                  <Dialog open={transplantOpen} onOpenChange={setTransplantOpen}>
                    <DialogTrigger asChild>
                      <Button
                        data-testid="transplant-settings-btn"
                        variant="outline"
                        className="rounded-full bg-gradient-to-r from-pink-50 to-purple-50 border-purple-200 hover:from-pink-100 hover:to-purple-100"
                      >
                        <span className="mr-1">💜</span>
                        <span className="mr-1">🎗️</span>
                        Transplant
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                      <TransplantSettings onClose={() => {
                        setTransplantOpen(false);
                        fetchTransplantInfo();
                      }} />
                    </DialogContent>
                  </Dialog>

                  <Button
                    data-testid="logout-btn"
                    variant="outline"
                    onClick={handleLogout}
                    className="rounded-full text-destructive hover:text-destructive"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* About You Section */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold flex items-center gap-2" style={{ color: displayTheme.primary_color }}>
            <User className="w-6 h-6" />
            About {isOwnProfile ? "Me" : profileUser.name}
          </h2>
          {isOwnProfile && (
            <Button
              onClick={() => setAboutOpen(true)}
              variant="outline"
              className="rounded-full"
              data-testid="edit-about-btn"
            >
              <Edit2 className="w-4 h-4 mr-2" />
              Edit About Me
            </Button>
          )}
        </div>

        {/* Display About You Info */}
        <div className="grid md:grid-cols-2 gap-4">
          {/* Motivations */}
          {aboutYou.motivations?.length > 0 && (
            <div className={`${cardClass} p-4 shadow-md border border-border/50`} style={{ backgroundColor: displayTheme.card_style === "glass" ? "rgba(255,255,255,0.7)" : "var(--card)" }}>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Star className="w-5 h-5" style={{ color: displayTheme.primary_color }} />
                What Motivates Me
              </h3>
              <div className="flex flex-wrap gap-2">
                {aboutYou.motivations.map((item, i) => (
                  <Badge key={i} variant="secondary">{item}</Badge>
                ))}
              </div>
            </div>
          )}

          {/* Likes */}
          {aboutYou.likes?.length > 0 && (
            <div className={`${cardClass} p-4 shadow-md border border-border/50`} style={{ backgroundColor: displayTheme.card_style === "glass" ? "rgba(255,255,255,0.7)" : "var(--card)" }}>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Heart className="w-5 h-5 text-pink-500" />
                Things I Love
              </h3>
              <div className="flex flex-wrap gap-2">
                {aboutYou.likes.map((item, i) => (
                  <Badge key={i} variant="secondary">{item}</Badge>
                ))}
              </div>
            </div>
          )}

          {/* Favorite Movies */}
          {aboutYou.favorite_movies?.length > 0 && (
            <div className={`${cardClass} p-4 shadow-md border border-border/50`} style={{ backgroundColor: displayTheme.card_style === "glass" ? "rgba(255,255,255,0.7)" : "var(--card)" }}>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Film className="w-5 h-5 text-purple-500" />
                Inspirational Movies
              </h3>
              <div className="flex flex-wrap gap-2">
                {aboutYou.favorite_movies.map((item, i) => (
                  <Badge key={i} variant="outline">{item}</Badge>
                ))}
              </div>
            </div>
          )}

          {/* Favorite Songs */}
          {aboutYou.favorite_songs?.length > 0 && (
            <div className={`${cardClass} p-4 shadow-md border border-border/50`} style={{ backgroundColor: displayTheme.card_style === "glass" ? "rgba(255,255,255,0.7)" : "var(--card)" }}>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Music className="w-5 h-5 text-green-500" />
                Favorite Songs
              </h3>
              <div className="flex flex-wrap gap-2">
                {aboutYou.favorite_songs.map((item, i) => (
                  <Badge key={i} variant="outline">{item}</Badge>
                ))}
              </div>
            </div>
          )}

          {/* Music Groups */}
          {aboutYou.favorite_music_groups?.length > 0 && (
            <div className={`${cardClass} p-4 shadow-md border border-border/50`} style={{ backgroundColor: displayTheme.card_style === "glass" ? "rgba(255,255,255,0.7)" : "var(--card)" }}>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Music className="w-5 h-5 text-blue-500" />
                Music Artists & Groups
              </h3>
              <div className="flex flex-wrap gap-2">
                {aboutYou.favorite_music_groups.map((item, i) => (
                  <Badge key={i} variant="outline">{item}</Badge>
                ))}
              </div>
            </div>
          )}

          {/* Places to Visit */}
          {aboutYou.places_to_visit?.length > 0 && (
            <div className={`${cardClass} p-4 shadow-md border border-border/50`} style={{ backgroundColor: displayTheme.card_style === "glass" ? "rgba(255,255,255,0.7)" : "var(--card)" }}>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-red-500" />
                Dream Destinations
              </h3>
              <div className="flex flex-wrap gap-2">
                {aboutYou.places_to_visit.map((item, i) => (
                  <Badge key={i} variant="outline">{item}</Badge>
                ))}
              </div>
            </div>
          )}

          {/* Favorite Books */}
          {aboutYou.favorite_books?.length > 0 && (
            <div className={`${cardClass} p-4 shadow-md border border-border/50`} style={{ backgroundColor: displayTheme.card_style === "glass" ? "rgba(255,255,255,0.7)" : "var(--card)" }}>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-amber-600" />
                Favorite Books
              </h3>
              <div className="flex flex-wrap gap-2">
                {aboutYou.favorite_books.map((item, i) => (
                  <Badge key={i} variant="outline">{item}</Badge>
                ))}
              </div>
            </div>
          )}

          {/* Hobbies */}
          {aboutYou.hobbies?.length > 0 && (
            <div className={`${cardClass} p-4 shadow-md border border-border/50`} style={{ backgroundColor: displayTheme.card_style === "glass" ? "rgba(255,255,255,0.7)" : "var(--card)" }}>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-cyan-500" />
                Hobbies & Interests
              </h3>
              <div className="flex flex-wrap gap-2">
                {aboutYou.hobbies.map((item, i) => (
                  <Badge key={i} variant="secondary">{item}</Badge>
                ))}
              </div>
            </div>
          )}

          {/* Favorite Quotes */}
          {aboutYou.favorite_quotes?.length > 0 && (
            <div className={`${cardClass} p-4 shadow-md border border-border/50 md:col-span-2`} style={{ backgroundColor: displayTheme.card_style === "glass" ? "rgba(255,255,255,0.7)" : "var(--card)" }}>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Quote className="w-5 h-5" style={{ color: displayTheme.primary_color }} />
                Favorite Quotes
              </h3>
              <div className="space-y-2">
                {aboutYou.favorite_quotes.map((quote, i) => (
                  <p key={i} className="text-sm italic text-muted-foreground border-l-2 pl-3" style={{ borderColor: displayTheme.primary_color }}>
                    &ldquo;{quote}&rdquo;
                  </p>
                ))}
              </div>
            </div>
          )}

          {/* Life Goals */}
          {aboutYou.life_goals && (
            <div className={`${cardClass} p-4 shadow-md border border-border/50 md:col-span-2`} style={{ backgroundColor: displayTheme.card_style === "glass" ? "rgba(255,255,255,0.7)" : "var(--card)" }}>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Target className="w-5 h-5" style={{ color: displayTheme.secondary_color }} />
                Life Mission
              </h3>
              <p className="text-muted-foreground">{aboutYou.life_goals}</p>
            </div>
          )}
        </div>

        {/* Empty state when no about info */}
        {isOwnProfile && !aboutYou.motivations?.length && !aboutYou.likes?.length && !aboutYou.favorite_movies?.length && (
          <div className={`${cardClass} p-8 shadow-md border border-border/50 text-center`} style={{ backgroundColor: displayTheme.card_style === "glass" ? "rgba(255,255,255,0.7)" : "var(--card)" }}>
            <User className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-semibold mb-2">Tell us about yourself!</h3>
            <p className="text-muted-foreground text-sm mb-4">
              Share your interests, favorite things, and what inspires you.
            </p>
            <Button onClick={() => setAboutOpen(true)} className="btn-primary">
              <Plus className="w-4 h-4 mr-2" />
              Add About Me
            </Button>
          </div>
        )}
      </div>

      {/* Profile Wall */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold flex items-center gap-2" style={{ color: displayTheme.primary_color }}>
            <MessageSquare className="w-6 h-6" />
            Wall
          </h2>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <Users className="w-4 h-4" />
              {friendCount} friends
            </span>
          </div>
        </div>

        {/* Friend action buttons for other profiles */}
        {!isOwnProfile && (
          <div className="mb-4 flex gap-2">
            {friendshipStatus === "none" && (
              <Button
                onClick={sendFriendRequest}
                className="bg-amber-500 hover:bg-amber-600"
                data-testid="add-friend-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Add Friend
              </Button>
            )}
            {friendshipStatus === "pending_sent" && (
              <Button disabled variant="secondary">
                <Loader2 className="w-4 h-4 mr-2" />
                Request Pending
              </Button>
            )}
            {friendshipStatus === "friends" && (
              <Badge className="bg-green-100 text-green-700 px-3 py-2">
                <Users className="w-4 h-4 mr-2" />
                Friends
              </Badge>
            )}
          </div>
        )}

        {/* Wall post input - show for own profile or friends */}
        {(isOwnProfile || friendshipStatus === "friends") && (
          <div className={`${cardClass} p-4 shadow-md border border-border/50 mb-4`} style={{ backgroundColor: displayTheme.card_style === "glass" ? "rgba(255,255,255,0.7)" : "var(--card)" }}>
            <div className="flex gap-3">
              <Avatar className="w-10 h-10">
                <AvatarImage src={getPhotoUrl(user?.profile_photo)} />
                <AvatarFallback className="bg-amber-500 text-white">
                  {user?.name?.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <Textarea
                  value={wallPostContent}
                  onChange={(e) => setWallPostContent(e.target.value)}
                  placeholder={isOwnProfile ? "What's on your mind?" : `Write something nice to ${profileUser?.name}...`}
                  className="min-h-[80px] resize-none"
                  data-testid="wall-post-input"
                />
                <div className="flex justify-end mt-2">
                  <Button
                    onClick={postToWall}
                    disabled={!wallPostContent.trim() || postingToWall}
                    className="bg-amber-500 hover:bg-amber-600"
                    data-testid="post-to-wall-btn"
                  >
                    {postingToWall ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Post
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Not friends message */}
        {!isOwnProfile && friendshipStatus !== "friends" && (
          <div className={`${cardClass} p-6 shadow-md border border-border/50 mb-4 text-center`} style={{ backgroundColor: displayTheme.card_style === "glass" ? "rgba(255,255,255,0.7)" : "var(--card)" }}>
            <Users className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
            <p className="text-muted-foreground">
              Add {profileUser?.name} as a friend to post on their wall
            </p>
          </div>
        )}

        {/* Wall posts list */}
        {loadingWall ? (
          <div className="text-center py-8">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-amber-500" />
          </div>
        ) : wallPosts.length === 0 ? (
          <div className={`${cardClass} p-8 shadow-md border border-border/50 text-center`} style={{ backgroundColor: displayTheme.card_style === "glass" ? "rgba(255,255,255,0.7)" : "var(--card)" }}>
            <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
            <p className="text-muted-foreground">No wall posts yet</p>
            {isOwnProfile && (
              <p className="text-sm text-muted-foreground mt-1">
                Friends can leave messages here!
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-4" data-testid="wall-posts-list">
            {wallPosts.map((wallPost) => (
              <div
                key={wallPost.id}
                className={`${cardClass} p-4 shadow-md border border-border/50`}
                style={{ backgroundColor: displayTheme.card_style === "glass" ? "rgba(255,255,255,0.7)" : "var(--card)" }}
                data-testid={`wall-post-${wallPost.id}`}
              >
                <div className="flex gap-3">
                  <Avatar
                    className="w-10 h-10 cursor-pointer"
                    onClick={() => navigate(`/profile/${wallPost.author_id}`)}
                  >
                    <AvatarImage src={getPhotoUrl(wallPost.author_photo)} />
                    <AvatarFallback className="bg-amber-500 text-white">
                      {wallPost.author_name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div>
                        <span
                          className="font-semibold cursor-pointer hover:text-amber-600"
                          onClick={() => navigate(`/profile/${wallPost.author_id}`)}
                        >
                          {wallPost.author_name}
                        </span>
                        <span className="text-xs text-muted-foreground ml-2">
                          {new Date(wallPost.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      {(wallPost.author_id === user?.id || isOwnProfile) && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-red-500"
                          onClick={() => deleteWallPost(wallPost.id)}
                          data-testid={`delete-wall-post-${wallPost.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    <p className="mt-2 text-foreground">{wallPost.content}</p>
                    <div className="flex items-center gap-4 mt-3">
                      <button
                        onClick={() => likeWallPost(wallPost.id)}
                        className="flex items-center gap-1 text-sm text-muted-foreground hover:text-pink-500 transition-colors"
                        data-testid={`like-wall-post-${wallPost.id}`}
                      >
                        <Heart className={`w-4 h-4 ${wallPost.likes > 0 ? "fill-pink-500 text-pink-500" : ""}`} />
                        {wallPost.likes > 0 && wallPost.likes}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Posts */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h2 className="text-2xl font-bold mb-6" style={{ color: displayTheme.primary_color }}>
          Posts
        </h2>
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
          </div>
        ) : posts.length === 0 ? (
          <div className="card-happy text-center py-12">
            <p className="text-muted-foreground">No posts yet</p>
          </div>
        ) : (
          <div className="space-y-6">
            {posts.map((post) => (
              <PostCard
                key={post.id}
                post={post}
                onReaction={handleReaction}
                onDelete={isOwnProfile ? handleDelete : null}
              />
            ))}
          </div>
        )}
      </div>

      {/* Customization Dialog */}
      <Dialog open={customizeOpen} onOpenChange={setCustomizeOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Palette className="w-5 h-5" />
              Customize Your Profile
            </DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="themes" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="themes">Themes</TabsTrigger>
              <TabsTrigger value="colors">Colors</TabsTrigger>
              <TabsTrigger value="style">Style</TabsTrigger>
              <TabsTrigger value="banner">Banner</TabsTrigger>
            </TabsList>

            {/* Theme Presets */}
            <TabsContent value="themes" className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Choose a preset theme or customize your own colors
              </p>
              <div className="grid grid-cols-2 gap-3">
                {themePresets.presets &&
                  Object.entries(themePresets.presets).map(([key, preset]) => (
                    <button
                      key={key}
                      onClick={() => applyPreset(key)}
                      data-testid={`theme-preset-${key}`}
                      className={`p-4 rounded-xl border-2 transition-all text-left ${
                        currentTheme.preset_theme === key
                          ? "ring-2 ring-primary border-primary"
                          : "border-border hover:border-primary/50"
                      }`}
                      style={{ background: preset.background_value }}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div
                          className="w-6 h-6 rounded-full"
                          style={{ backgroundColor: preset.primary_color }}
                        />
                        <div
                          className="w-6 h-6 rounded-full"
                          style={{ backgroundColor: preset.secondary_color }}
                        />
                        {currentTheme.preset_theme === key && (
                          <Check className="w-4 h-4 text-primary ml-auto" />
                        )}
                      </div>
                      <p className="font-medium text-foreground">{preset.name}</p>
                    </button>
                  ))}
              </div>
            </TabsContent>

            {/* Custom Colors */}
            <TabsContent value="colors" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="primary-color">Primary Color</Label>
                  <div className="flex gap-2 mt-2">
                    <input
                      type="color"
                      id="primary-color"
                      value={currentTheme.primary_color}
                      onChange={(e) =>
                        setCurrentTheme((prev) => ({
                          ...prev,
                          primary_color: e.target.value,
                          preset_theme: null,
                        }))
                      }
                      className="w-12 h-10 rounded cursor-pointer"
                      data-testid="primary-color-picker"
                    />
                    <Input
                      value={currentTheme.primary_color}
                      onChange={(e) =>
                        setCurrentTheme((prev) => ({
                          ...prev,
                          primary_color: e.target.value,
                          preset_theme: null,
                        }))
                      }
                      className="flex-1"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="secondary-color">Secondary Color</Label>
                  <div className="flex gap-2 mt-2">
                    <input
                      type="color"
                      id="secondary-color"
                      value={currentTheme.secondary_color}
                      onChange={(e) =>
                        setCurrentTheme((prev) => ({
                          ...prev,
                          secondary_color: e.target.value,
                          preset_theme: null,
                        }))
                      }
                      className="w-12 h-10 rounded cursor-pointer"
                      data-testid="secondary-color-picker"
                    />
                    <Input
                      value={currentTheme.secondary_color}
                      onChange={(e) =>
                        setCurrentTheme((prev) => ({
                          ...prev,
                          secondary_color: e.target.value,
                          preset_theme: null,
                        }))
                      }
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>

              {/* Background Type */}
              <div>
                <Label>Background Type</Label>
                <div className="flex gap-2 mt-2">
                  {["solid", "gradient"].map((type) => (
                    <Button
                      key={type}
                      variant={currentTheme.background_type === type ? "default" : "outline"}
                      onClick={() =>
                        setCurrentTheme((prev) => ({
                          ...prev,
                          background_type: type,
                          background_value:
                            type === "solid"
                              ? prev.primary_color
                              : `linear-gradient(135deg, ${prev.primary_color}20 0%, ${prev.secondary_color}40 100%)`,
                        }))
                      }
                      className="capitalize"
                    >
                      {type}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Preview */}
              <div>
                <Label>Preview</Label>
                <div
                  className="h-24 rounded-xl mt-2"
                  style={{
                    background:
                      currentTheme.background_type === "solid"
                        ? currentTheme.primary_color
                        : currentTheme.background_value,
                  }}
                />
              </div>
            </TabsContent>

            {/* Style Options */}
            <TabsContent value="style" className="space-y-6">
              {/* Card Style */}
              <div>
                <Label>Card Style</Label>
                <div className="flex gap-3 mt-2">
                  {[
                    { id: "rounded", icon: Circle, label: "Rounded" },
                    { id: "sharp", icon: Square, label: "Sharp" },
                    { id: "glass", icon: Sparkles, label: "Glass" },
                  ].map((style) => (
                    <Button
                      key={style.id}
                      variant={currentTheme.card_style === style.id ? "default" : "outline"}
                      onClick={() =>
                        setCurrentTheme((prev) => ({ ...prev, card_style: style.id }))
                      }
                      className="flex-1"
                      data-testid={`card-style-${style.id}`}
                    >
                      <style.icon className="w-4 h-4 mr-2" />
                      {style.label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Font Style */}
              <div>
                <Label>Font Style</Label>
                <div className="flex gap-3 mt-2">
                  {Object.entries(FONT_STYLES).map(([key, style]) => (
                    <Button
                      key={key}
                      variant={currentTheme.font_style === key ? "default" : "outline"}
                      onClick={() =>
                        setCurrentTheme((prev) => ({ ...prev, font_style: key }))
                      }
                      className="flex-1"
                      style={{ fontFamily: style.family }}
                      data-testid={`font-style-${key}`}
                    >
                      <Type className="w-4 h-4 mr-2" />
                      {style.name}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Bio Alignment */}
              <div>
                <Label>Bio Alignment</Label>
                <div className="flex gap-3 mt-2">
                  {[
                    { id: "left", icon: AlignLeft, label: "Left" },
                    { id: "center", icon: AlignCenter, label: "Center" },
                    { id: "right", icon: AlignRight, label: "Right" },
                  ].map((align) => (
                    <Button
                      key={align.id}
                      variant={currentTheme.bio_alignment === align.id ? "default" : "outline"}
                      onClick={() =>
                        setCurrentTheme((prev) => ({ ...prev, bio_alignment: align.id }))
                      }
                      className="flex-1"
                      data-testid={`bio-align-${align.id}`}
                    >
                      <align.icon className="w-4 h-4 mr-2" />
                      {align.label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Avatar Border Style */}
              <div>
                <Label>Avatar Border Style</Label>
                <div className="grid grid-cols-5 gap-2 mt-2">
                  {[
                    { id: "solid", label: "Solid" },
                    { id: "double", label: "Double" },
                    { id: "glow", label: "Glow" },
                    { id: "rainbow", label: "Rainbow" },
                    { id: "none", label: "None" },
                  ].map((border) => (
                    <Button
                      key={border.id}
                      variant={currentTheme.avatar_border === border.id ? "default" : "outline"}
                      onClick={() =>
                        setCurrentTheme((prev) => ({ ...prev, avatar_border: border.id }))
                      }
                      className="text-xs px-2"
                      data-testid={`avatar-border-${border.id}`}
                    >
                      {border.label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Name Effect */}
              <div>
                <Label>Name Effect</Label>
                <div className="grid grid-cols-5 gap-2 mt-2">
                  {[
                    { id: "none", label: "None" },
                    { id: "gradient", label: "Gradient" },
                    { id: "glow", label: "Glow" },
                    { id: "shadow", label: "Shadow" },
                    { id: "outline", label: "Outline" },
                  ].map((effect) => (
                    <Button
                      key={effect.id}
                      variant={currentTheme.name_effect === effect.id ? "default" : "outline"}
                      onClick={() =>
                        setCurrentTheme((prev) => ({ ...prev, name_effect: effect.id }))
                      }
                      className="text-xs px-2"
                      data-testid={`name-effect-${effect.id}`}
                    >
                      {effect.label}
                    </Button>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* Banner Upload */}
            <TabsContent value="banner" className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Upload a custom banner image for your profile header
              </p>

              {currentTheme.banner_image || displayTheme.banner_image ? (
                <div className="space-y-3">
                  <div
                    className="h-32 rounded-xl bg-cover bg-center"
                    style={{
                      backgroundImage: `url(${getPhotoUrl(currentTheme.banner_image || displayTheme.banner_image)})`,
                    }}
                  />
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setCustomizeOpen(false);
                        setTimeout(() => bannerInputRef.current?.click(), 100);
                      }}
                      disabled={uploadingBanner}
                      className="flex-1"
                    >
                      {uploadingBanner ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4 mr-2" />
                      )}
                      Change Banner
                    </Button>
                    <Button
                      variant="outline"
                      onClick={async () => {
                        setCurrentTheme((prev) => ({ ...prev, banner_image: null }));
                        // Also clear from database
                        try {
                          await axios.put(`${API}/users/theme`, { ...currentTheme, banner_image: null });
                          setProfileUser((prev) => ({
                            ...prev,
                            theme: { ...(prev.theme || {}), banner_image: null }
                          }));
                          toast.success("Banner removed");
                        } catch (e) {
                          console.error(e);
                        }
                      }}
                      className="text-destructive"
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => {
                    setCustomizeOpen(false);
                    setTimeout(() => bannerInputRef.current?.click(), 100);
                  }}
                  disabled={uploadingBanner}
                  className="w-full h-32 border-2 border-dashed border-border rounded-xl flex flex-col items-center justify-center hover:border-primary transition-colors"
                  data-testid="upload-banner-btn"
                >
                  {uploadingBanner ? (
                    <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                  ) : (
                    <>
                      <Image className="w-8 h-8 text-muted-foreground mb-2" />
                      <p className="text-muted-foreground">Click to upload banner</p>
                      <p className="text-xs text-muted-foreground">Max 10MB</p>
                    </>
                  )}
                </button>
              )}

              <p className="text-xs text-muted-foreground">
                Recommended size: 1500 x 500 pixels
              </p>
            </TabsContent>
          </Tabs>

          <div className="flex gap-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => setCustomizeOpen(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveTheme}
              disabled={savingTheme}
              className="flex-1 btn-primary"
              data-testid="save-theme-btn"
            >
              {savingTheme ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Save Theme
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* About You Dialog */}
      <Dialog open={aboutOpen} onOpenChange={setAboutOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Edit About Me
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Motivations */}
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <Star className="w-4 h-4 text-amber-500" />
                What Motivates You
              </Label>
              <div className="flex gap-2 mb-2">
                <Input
                  placeholder="e.g., Family, Success, Making a difference..."
                  value={newItem.motivations || ""}
                  onChange={(e) => setNewItem(prev => ({ ...prev, motivations: e.target.value }))}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addToList("motivations"))}
                  className="input-happy"
                />
                <Button onClick={() => addToList("motivations")} size="icon" variant="outline">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {aboutYou.motivations?.map((item, i) => (
                  <Badge key={i} variant="secondary" className="py-1 px-2">
                    {item}
                    <button onClick={() => removeFromList("motivations", i)} className="ml-1">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            {/* Likes */}
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <Heart className="w-4 h-4 text-pink-500" />
                Things You Love
              </Label>
              <div className="flex gap-2 mb-2">
                <Input
                  placeholder="e.g., Sunsets, Coffee, Helping others..."
                  value={newItem.likes || ""}
                  onChange={(e) => setNewItem(prev => ({ ...prev, likes: e.target.value }))}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addToList("likes"))}
                  className="input-happy"
                />
                <Button onClick={() => addToList("likes")} size="icon" variant="outline">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {aboutYou.likes?.map((item, i) => (
                  <Badge key={i} variant="secondary" className="py-1 px-2">
                    {item}
                    <button onClick={() => removeFromList("likes", i)} className="ml-1">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            {/* Favorite Movies */}
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <Film className="w-4 h-4 text-purple-500" />
                Inspirational Movies
              </Label>
              <div className="flex gap-2 mb-2">
                <Input
                  placeholder="e.g., The Pursuit of Happyness, Soul..."
                  value={newItem.favorite_movies || ""}
                  onChange={(e) => setNewItem(prev => ({ ...prev, favorite_movies: e.target.value }))}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addToList("favorite_movies"))}
                  className="input-happy"
                />
                <Button onClick={() => addToList("favorite_movies")} size="icon" variant="outline">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {aboutYou.favorite_movies?.map((item, i) => (
                  <Badge key={i} variant="outline" className="py-1 px-2">
                    {item}
                    <button onClick={() => removeFromList("favorite_movies", i)} className="ml-1">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            {/* Favorite Songs */}
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <Music className="w-4 h-4 text-green-500" />
                Favorite Songs
              </Label>
              <div className="flex gap-2 mb-2">
                <Input
                  placeholder="e.g., Happy by Pharrell, Don't Stop Believin'..."
                  value={newItem.favorite_songs || ""}
                  onChange={(e) => setNewItem(prev => ({ ...prev, favorite_songs: e.target.value }))}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addToList("favorite_songs"))}
                  className="input-happy"
                />
                <Button onClick={() => addToList("favorite_songs")} size="icon" variant="outline">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {aboutYou.favorite_songs?.map((item, i) => (
                  <Badge key={i} variant="outline" className="py-1 px-2">
                    {item}
                    <button onClick={() => removeFromList("favorite_songs", i)} className="ml-1">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            {/* Music Groups */}
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <Music className="w-4 h-4 text-blue-500" />
                Favorite Artists & Groups
              </Label>
              <div className="flex gap-2 mb-2">
                <Input
                  placeholder="e.g., Coldplay, John Legend..."
                  value={newItem.favorite_music_groups || ""}
                  onChange={(e) => setNewItem(prev => ({ ...prev, favorite_music_groups: e.target.value }))}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addToList("favorite_music_groups"))}
                  className="input-happy"
                />
                <Button onClick={() => addToList("favorite_music_groups")} size="icon" variant="outline">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {aboutYou.favorite_music_groups?.map((item, i) => (
                  <Badge key={i} variant="outline" className="py-1 px-2">
                    {item}
                    <button onClick={() => removeFromList("favorite_music_groups", i)} className="ml-1">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            {/* Places to Visit */}
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <MapPin className="w-4 h-4 text-red-500" />
                Dream Destinations
              </Label>
              <div className="flex gap-2 mb-2">
                <Input
                  placeholder="e.g., Bali, Paris, Machu Picchu..."
                  value={newItem.places_to_visit || ""}
                  onChange={(e) => setNewItem(prev => ({ ...prev, places_to_visit: e.target.value }))}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addToList("places_to_visit"))}
                  className="input-happy"
                />
                <Button onClick={() => addToList("places_to_visit")} size="icon" variant="outline">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {aboutYou.places_to_visit?.map((item, i) => (
                  <Badge key={i} variant="outline" className="py-1 px-2">
                    {item}
                    <button onClick={() => removeFromList("places_to_visit", i)} className="ml-1">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            {/* Favorite Books */}
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <BookOpen className="w-4 h-4 text-amber-600" />
                Favorite Books
              </Label>
              <div className="flex gap-2 mb-2">
                <Input
                  placeholder="e.g., The Alchemist, Think and Grow Rich..."
                  value={newItem.favorite_books || ""}
                  onChange={(e) => setNewItem(prev => ({ ...prev, favorite_books: e.target.value }))}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addToList("favorite_books"))}
                  className="input-happy"
                />
                <Button onClick={() => addToList("favorite_books")} size="icon" variant="outline">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {aboutYou.favorite_books?.map((item, i) => (
                  <Badge key={i} variant="outline" className="py-1 px-2">
                    {item}
                    <button onClick={() => removeFromList("favorite_books", i)} className="ml-1">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            {/* Hobbies */}
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-cyan-500" />
                Hobbies & Interests
              </Label>
              <div className="flex gap-2 mb-2">
                <Input
                  placeholder="e.g., Photography, Hiking, Cooking..."
                  value={newItem.hobbies || ""}
                  onChange={(e) => setNewItem(prev => ({ ...prev, hobbies: e.target.value }))}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addToList("hobbies"))}
                  className="input-happy"
                />
                <Button onClick={() => addToList("hobbies")} size="icon" variant="outline">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {aboutYou.hobbies?.map((item, i) => (
                  <Badge key={i} variant="secondary" className="py-1 px-2">
                    {item}
                    <button onClick={() => removeFromList("hobbies", i)} className="ml-1">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            {/* Favorite Quotes */}
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <Quote className="w-4 h-4 text-purple-500" />
                Favorite Quotes
              </Label>
              <div className="flex gap-2 mb-2">
                <Input
                  placeholder="Add an inspiring quote..."
                  value={newItem.favorite_quotes || ""}
                  onChange={(e) => setNewItem(prev => ({ ...prev, favorite_quotes: e.target.value }))}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addToList("favorite_quotes"))}
                  className="input-happy"
                />
                <Button onClick={() => addToList("favorite_quotes")} size="icon" variant="outline">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="space-y-2">
                {aboutYou.favorite_quotes?.map((quote, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm italic text-muted-foreground bg-muted/50 p-2 rounded">
                    <span className="flex-1">&ldquo;{quote}&rdquo;</span>
                    <button onClick={() => removeFromList("favorite_quotes", i)}>
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Life Goals */}
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <Target className="w-4 h-4 text-green-500" />
                Life Mission / Goals
              </Label>
              <Textarea
                placeholder="What's your life mission? What drives you every day?"
                value={aboutYou.life_goals || ""}
                onChange={(e) => setAboutYou(prev => ({ ...prev, life_goals: e.target.value }))}
                className="input-happy"
              />
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => setAboutOpen(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={saveAboutYou}
              disabled={savingAbout}
              className="flex-1 btn-primary"
              data-testid="save-about-btn"
            >
              {savingAbout ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Save About Me
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Navigation />
    </div>
  );
}
